import React from 'react';
import sendMessageChangelog from './send-message.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType, SendMessageNodeState } from '../..';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Send Message',
  identifier: 'SendMessage',
  command_id: 'message.initiated',
  icon: 'telnyx',
  background: '#000000',
  changelog: sendMessageChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'message.send.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'message.send.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      id: 'message.initiated',
      label: 'Executed',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'message.initiated.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [],
};

export const state: SendMessageNodeState = {
  type: 'SMS',
  messaging_profile_id: '',
  from: { type: NodeInputType.Expression, value: '' },
  to: { type: NodeInputType.Expression, value: '' },
  text: { type: NodeInputType.Expression, value: '' },
  subject: { type: NodeInputType.Expression, value: '' },
  media_urls: { type: NodeInputType.MediaUrl, value: [] },
  webhook_url: { type: NodeInputType.Expression, value: '' },
  webhook_failover_url: { type: NodeInputType.Expression, value: '' },
  use_profile_webhooks: 'true',
  auto_detect: 'true',
};

export const fields: NodeField[] = [
  {
    id: 'type',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Type',
    description: 'The protocol for sending the message, either SMS or MMS.',
    required: true,
    options: [
      { label: 'SMS', value: 'SMS' },
      { label: 'MMS', value: 'MMS' },
    ],
  },
  {
    id: 'messaging_profile_id',
    tab: '1',
    type: NodeFieldType.MessagingProfile,
    label: 'Messaging Profile ID',
    description:
      'Unique identifier for a messaging profile. Required if sending via number pool or with an alphanumeric sender ID.',
    required: false,
  },
  {
    id: 'from',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'From',
    description:
      'Sending address (+E.164 formatted phone number, alphanumeric sender ID, or short code). Required if sending with a phone number, short code, or alphanumeric sender ID.',
    placeholder: 'From (+E.164 number, alphanumeric sender ID, or short code)',
    required: false,
  },
  {
    id: 'to',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'To',
    description: 'Receiving address (+E.164 formatted phone number or short code).',
    placeholder: 'To (+E.164 number or short code)',
    required: true,
  },
  {
    id: 'text',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Text',
    description: 'Message body (i.e., content) as a non-empty string.',
    placeholder: 'Hello World!',
    required: true,
  },
  {
    id: 'subject',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Subject',
    description: 'Subject of multimedia message.',
    placeholder: 'MMS Subject',
    required: true,
  },
  {
    id: 'media_urls',
    tab: '1',
    type: NodeFieldType.MediaUrls,
    label: 'Media URLs',
    description: 'A list of media URLs. The total media size must be less than 1 MB.',
    required: true,
  },
  {
    id: 'use_profile_webhooks',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Use Profile Webhooks',
    description:
      'If the profile this number is associated with has webhooks, use them for delivery notifications. If webhooks are also specified on the message itself, they will be attempted first, then those on the profile.',
    required: false,
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
  },
  {
    id: 'webhook_url',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Webhook URL',
    description: 'The URL where webhooks related to this message will be sent.',
    placeholder: 'https://example.com',
    required: false,
  },
  {
    id: 'webhook_failover_url',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Failover Webhook URL',
    description:
      'The failover URL where webhooks related to this message will be sent if sending to the primary URL fails.',
    placeholder: 'https://example.com',
    required: false,
  },
  {
    id: 'auto_detect',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Auto Detect',
    description:
      'Automatically detect if an SMS message is unusually long and exceeds a recommended limit of message parts.',
    required: false,
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
  },
];

export const output: NodeOutput = {
  default: {
    message: {
      initiated: {
        record_type: 'message',
        direction: 'outbound',
        id: 'string',
        type: 'SMS | MMS',
        message_profile_id: 'string',
        organization_id: 'string',
        from: {
          phone_number: 'string',
          carrier: 'string',
          line_type: ' string',
        },
        to: [
          {
            phone_number: 'string',
            status: 'string',
            carrier: 'string',
            line_type: ' string',
          },
        ],
        text: 'string',
        subject: 'string',
        media: [
          {
            url: 'string',
            content_type: 'string',
            sha256: 'string',
            size: 'number',
          },
        ],
        webhook_url: 'string',
        webhook_failover_url: 'string',
        encoding: 'string',
        parts: 'number',
        tags: ['string'],
        cost: {
          amount: 'number',
          currency: 'string',
        },
        cost_breakdown: {
          rate: {
            amount: 'number',
            currency: 'string',
          },
          carrier_fee: {
            amount: 'number',
            currency: 'string',
          },
        },
        received_at: 'string',
        sent_at: 'string',
        completed_at: 'string',
        valid_until: 'string',
        errors: [
          {
            code: 'number',
            title: 'string',
            detail: 'string',
          },
        ],
      },
    },
  },
  dynamic: null,
};

export const SendMessage: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'message.initiated') return;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
